import React, { useState, useEffect } from 'react';
import { rbacUtils } from '../../../services/rbacApi';
import TextInput from '../../Common/TextInput';

// Icon components
const CodeIcon = (props) => (
  <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
  </svg>
);

const NameIcon = (props) => (
  <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
  </svg>
);

const ModuleIcon = (props) => (
  <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
  </svg>
);

const ActionIcon = (props) => (
  <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
);

const ResourceIcon = (props) => (
  <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" />
  </svg>
);

const PermissionForm = ({ 
  permission, 
  onSubmit, 
  onCancel, 
  modules = [], 
  actions = [] 
}) => {
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    module: '',
    action: '',
    resource: '',
    isActive: true
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (permission) {
      setFormData({
        code: permission.code || '',
        name: permission.name || '',
        description: permission.description || '',
        module: permission.module || '',
        action: permission.action || '',
        resource: permission.resource || '',
        isActive: permission.isActive !== false
      });
    }
  }, [permission]);

  const validateForm = () => {
    const newErrors = {};

    // Code validation
    if (!formData.code.trim()) {
      newErrors.code = 'Permission code is required';
    } else if (!rbacUtils.isValidPermissionCode(formData.code)) {
      newErrors.code = 'Permission code contains unsupported characters';
    }

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Permission name is required';
    } else if (formData.name.length < 3) {
      newErrors.name = 'Permission name must be at least 3 characters';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Permission name must be less than 100 characters';
    }

    // Module validation
    if (!formData.module.trim()) {
      newErrors.module = 'Module is required';
    }

    // Action validation  
    if (!formData.action.trim()) {
      newErrors.action = 'Action is required';
    }

    // Resource validation
    if (!formData.resource.trim()) {
      newErrors.resource = 'Resource is required';
    }

    // Description validation (optional but if provided should be reasonable length)
    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Description must be less than 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Convert Resource, Action, Module fields to uppercase
    let processedValue = value;
    if ((name === 'module' || name === 'action' || name === 'resource') && type !== 'checkbox') {
      processedValue = value.toUpperCase();
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : processedValue
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Auto-generate code when module, resource, or action changes
    if (name === 'module' || name === 'resource' || name === 'action') {
      const updatedData = { ...formData, [name]: processedValue };
      if (updatedData.module && updatedData.resource && updatedData.action) {
        const autoCode = rbacUtils.buildPermissionCode(updatedData.module, updatedData.resource, updatedData.action);
        setFormData(prev => ({ ...prev, [name]: processedValue, code: autoCode }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      setErrors({ submit: error.message || 'Failed to save permission' });
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="fixed inset-0 backdrop-blur-sm bg-black/20 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-2">
      <div className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl border border-gray-100 max-h-[95vh] overflow-y-auto">
        {/* Compact Header */}
        <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-2xl px-4 py-3 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white">
                {permission ? 'Edit Permission' : 'Create Permission'}
              </h3>
            </div>
            <button
              onClick={onCancel}
              className="w-7 h-7 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-all"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-4">
        {errors.submit && (
          <div className="mb-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-center">
              <svg className="h-4 w-4 text-red-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm font-medium text-red-800">{errors.submit}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Main Fields Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Left Column */}
            <div className="space-y-4">
              {/* Permission Code */}
              <TextInput
                label="Permission Code"
                id="code"
                name="code"
                value={formData.code}
                onChange={handleChange}
                placeholder="USERS:PROFILE:READ"
                icon={CodeIcon}
                error={errors.code}
                required
                variant="input"
              />

              {/* Permission Name */}
              <TextInput
                label="Permission Name"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="View User Profile"
                icon={NameIcon}
                error={errors.name}
                required
                variant="input"
              />

              {/* Description */}
              <TextInput
                label="Description"
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Describe what this permission allows..."
                error={errors.description}
                variant="textarea"
                rows={4}
                maxLength={500}
                showCharCount={true}
              />
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              {/* Module */}
              <TextInput
                label="Module"
                id="module"
                name="module"
                value={formData.module}
                onChange={handleChange}
                placeholder="USERS"
                icon={ModuleIcon}
                error={errors.module}
                required
                variant="input"
              />

              {/* Action */}
              <TextInput
                label="Action"
                id="action"
                name="action"
                value={formData.action}
                onChange={handleChange}
                placeholder="READ"
                icon={ActionIcon}
                error={errors.action}
                required
                variant="input"
              />

              {/* Resource */}
              <TextInput
                label="Resource"
                id="resource"
                name="resource"
                value={formData.resource}
                onChange={handleChange}
                placeholder="PROFILE"
                icon={ResourceIcon}
                error={errors.resource}
                required
                variant="input"
              />

              {/* Active Status */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  <input
                    id="isActive"
                    name="isActive"
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={handleChange}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isActive" className="ml-2 block text-sm font-medium text-gray-900">
                    Active Permission
                  </label>
                </div>
                <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  formData.isActive 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                    formData.isActive ? 'bg-green-500' : 'bg-gray-400'
                  }`}></div>
                  {formData.isActive ? 'Enabled' : 'Disabled'}
                </div>
              </div>
            </div>
          </div>

          {/* Compact Preview */}
          {formData.code && (
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  <span className="text-sm font-semibold text-indigo-900">Preview:</span>
                </div>
                <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  formData.isActive 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                    formData.isActive ? 'bg-green-500' : 'bg-gray-400'
                  }`}></div>
                  {formData.isActive ? 'Active' : 'Inactive'}
                </div>
              </div>
              <div className="mt-2">
                <div className="font-mono text-sm text-indigo-800 bg-white bg-opacity-60 rounded px-2 py-1">
                  {formData.code}
                </div>
                {formData.name && (
                  <div className="text-xs text-indigo-700 mt-1">{formData.name}</div>
                )}
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {loading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </div>
              ) : (
                <div className="flex items-center">
                  <svg className="w-3 h-3 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={permission ? "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" : "M12 4v16m8-8H4"} />
                  </svg>
                  {permission ? 'Update' : 'Create'}
                </div>
              )}
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

export default PermissionForm;